import React, { useEffect, useState, useContext } from "react";
import { notification } from "antd";
import { 
    MessageOutlined, 
    DollarOutlined, 
    BankOutlined,
    CustomerServiceOutlined,
} from "@ant-design/icons";
import { io } from "socket.io-client";
import { AuthContext } from "../../contexts/AuthContextProvider";
import { ROOT_URL } from "../../helpers/Constant";
import "./admin-notifications.scss";

const AdminNotifications = () => {
    const { profile, getUserProfile } = useContext(AuthContext);
    const [socket, setSocket] = useState(null);
    const [api, contextHolder] = notification.useNotification();

    useEffect(() => {
        // Fetch profile if not loaded
        if (!profile) {
            getUserProfile();
            return;
        }

        // Only connect if user is admin
        if (!profile || (profile.type !== "Admin" && profile.role !== "admin")) {
            console.log("User is not admin, skipping notification socket connection");
            console.log("Profile:", profile);
            return;
        }

        console.log("✅ User is admin! Initializing notification socket");
        console.log("Admin ID:", profile._id);
        console.log("Admin Name:", profile.fullName);
        console.log("Connecting to:", ROOT_URL || "http://localhost:8001");

        // Initialize socket connection
        const socketInstance = io(ROOT_URL || "http://localhost:8001", {
            auth: {
                userId: profile._id,
            },
            transports: ["polling", "websocket"],
            reconnection: true,
            reconnectionDelay: 500,
            reconnectionDelayMax: 2000,
            reconnectionAttempts: 10,
        });

        socketInstance.on("connect", () => {
            console.log("✅ Admin socket connected successfully!");
            console.log("Socket ID:", socketInstance.id);
        });

        socketInstance.on("admin_notification", (data) => {
            console.log("🔔 Received admin notification:", data);
            
            // Check if this notification is for the current conversation
            // If admin is currently viewing this conversation, don't show notification
            if (data.type === "new_message" && data.data?.conversationId) {
                const currentConversationId = localStorage.getItem('admin_current_conversation_id');
                const notificationConversationId = data.data.conversationId?.toString();
                
                if (currentConversationId && currentConversationId === notificationConversationId) {
                    console.log("🔕 Ignoring notification - admin is currently viewing this conversation");
                    return; // Don't show notification
                }
            }
            
            showNotification(data);
        });

        socketInstance.on("disconnect", () => {
            console.log("❌ Admin socket disconnected");
        });

        socketInstance.on("connect_error", (error) => {
            console.error("🚫 Socket connection error:", error);
        });

        setSocket(socketInstance);

        return () => {
            console.log("Cleaning up admin notification socket");
            if (socketInstance) {
                socketInstance.disconnect();
            }
        };
    }, [profile, getUserProfile]);

    const showNotification = (data) => {
        console.log("📢 showNotification called with data:", data);
        const { type, title, message, timestamp } = data;

        let icon;
        let className = "admin-notification";

        switch (type) {
            case "new_message":
                icon = <MessageOutlined style={{ color: "#1890ff" }} />;
                className += " notification-message";
                break;
            case "new_deposit":
                icon = <DollarOutlined style={{ color: "#52c41a" }} />;
                className += " notification-deposit";
                break;
            case "new_withdraw":
                icon = <BankOutlined style={{ color: "#fa8c16" }} />;
                className += " notification-withdraw";
                break;
            case "new_support":
                icon = <CustomerServiceOutlined style={{ color: "#2758d1" }} />;
                className += " notification-support";
                break;
            default:
                icon = <MessageOutlined />;
        }

        console.log("🔔 Opening notification popup:", { title, message, type });
        
        // Determine redirect URL based on type
        let redirectUrl = "/";
        switch (type) {
            case "new_message":
                redirectUrl = "/chat";
                break;
            case "new_deposit":
                redirectUrl = "/depositRecord-list";
                break;
            case "new_withdraw":
                redirectUrl = "/withdrawRecord-list";
                break;
            case "new_support":
                redirectUrl = "/support-list";
                break;
        }
        
        api.open({
            message: title,
            description: message,
            icon,
            placement: "bottomRight",
            duration: 0, // Never auto-close (0 = manual close only)
            className,
            style: {
                width: 380,
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                cursor: "pointer",
            },
            onClick: () => {
                console.log("🔗 Notification clicked, redirecting to:", redirectUrl);
                window.location.href = redirectUrl;
            },
        });
        
        console.log("✅ Notification displayed");

        // Play notification sound (optional)
        try {
            const audio = new Audio("/notification.mp3");
            audio.volume = 0.5;
            audio.play().catch(() => {
                // Ignore error if sound fails to play
            });
        } catch (e) {
            // Ignore
        }
    };

    return contextHolder;
};

export default AdminNotifications;
